import { NgTemplateOutlet } from '@angular/common';
import { Component, DOCUMENT, effect, EffectRef, inject, input, InputSignal, ResourceRef, Signal, ChangeDetectionStrategy } from '@angular/core';
import { rxResource, toSignal } from '@angular/core/rxjs-interop';
import { MatCard } from '@angular/material/card';
import { SliderComponent, SliderSlideDirective } from '@libs/common/components';
import { CartModel, ProductModel } from '@libs/shop/models';
import { CartService } from '@libs/shared/services';
import { getTrackingPagePath, ProductTrackingListDirective } from '@libs/shared/tracking';
import { map } from 'rxjs';
import { AddToShoppingListDialogComponent } from '../../shopping-list/add-to-shopping-list-dialog/add-to-shopping-list-dialog.component';
import { ProductCardSkeletonComponent } from '../../catalog/product-card/product-card-skeleton/product-card-skeleton.component';
import { ProductCardSmallSkeletonComponent } from '../../catalog/product-card-small/product-card-small-skeleton/product-card-small-skeleton.component';
import { ProductSliderComponent } from '../../elements/product-slider/product-slider.component';
import { CrossSellingRepositoryService } from '../../services';

@Component({
  selector: 'ui-cross-selling-slider',
  templateUrl: './cross-selling-slider.component.html',
  styleUrl: 'cross-selling-slider.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    SliderComponent,
    SliderSlideDirective,
    AddToShoppingListDialogComponent,
    ProductSliderComponent,
    ProductCardSmallSkeletonComponent,
    MatCard,
    NgTemplateOutlet,
    ProductCardSkeletonComponent,
    ProductTrackingListDirective,
  ],
})
export class CrossSellingSliderComponent {
  public crossSellingRepositoryService: CrossSellingRepositoryService = inject(CrossSellingRepositoryService);
  public cartService: CartService = inject(CartService);

  public headline: InputSignal<string | undefined> = input<string | undefined>();
  public small: InputSignal<boolean> = input(false);

  public productsResource: ResourceRef<ProductModel[]> = rxResource({
    stream: () => this.crossSellingRepositoryService.getAll().pipe(map((result) => result.products)),
    defaultValue: [],
  });

  public readonly skeletonArray: number[] = new Array<number>(10).fill(0);

  protected readonly trackingList: string = `cross-selling_${getTrackingPagePath(inject(DOCUMENT)).slice(1)}`;

  private readonly cart: Signal<CartModel | undefined> = toSignal(this.cartService.getCart());

  private readonly reloadResourceEffect: EffectRef = effect(() => {
    const cart: CartModel | undefined = this.cart();

    if (cart) {
      const skus: string[] = this.productsResource.value().map(x => x.sku);
      const cartSkus: string[] = cart.items.map(x => x.sku);

      if (cartSkus.some(x => skus.includes(x))) {
        this.productsResource.reload();
      }
    }
  });
}
